import React, { useState, useEffect } from "react";
import {
  Users,
  Settings,
  FolderOpen,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Search,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Shield,
  UserCheck,
  UserX,
  Calendar,
  BarChart3,
  Clock,
  Eye,
  EyeOff,
  Building2,
  Upload,
  Archive,
  RotateCcw,
  Copy,
  Lock,
} from "lucide-react";
import GoogleDriveSyncPanel from "./GoogleDriveSyncPanel";
import { useSchool } from '../contexts/SchoolContext';

// ESUSD Logo Component
const EsusdLogo = ({ size = 48, className = "" }) => {
  return (
    <img
      src="/assets/esusd-logo.png"
      alt="ESUSD"
      width={size}
      height={size}
      className="rounded-full"
    />
  );
};

// Simple Analytics Component
const SimpleAnalytics = ({ user, token, onBack, schoolId, schoolYearId }) => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiCall = async (url, options = {}) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || "http://localhost:3001"}${url}`,
      {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Request failed");
    }

    return response.json();
  };

  useEffect(() => {
    fetchAnalytics();
  }, [schoolId, schoolYearId]);

  const fetchAnalytics = async () => {
    if (!schoolId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ schoolId });
      if (schoolYearId) params.append("schoolYearId", schoolYearId);
      const url = `/api/admin/analytics/activities?${params}`;
      const data = await apiCall(url);
      setAnalytics(data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">
          Activity Analytics
        </h2>
        <button
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 px-3 py-1 rounded-lg hover:bg-blue-50"
        >
          Back to Admin
        </button>
      </div>

      {!schoolId ? (
        <div className="text-center py-12 text-gray-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">Please select a school to view analytics</p>
          <p className="text-sm mt-2">Use the school filter dropdown to select a school</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
        </div>
      ) : analytics.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No analytics data available</p>
          <p className="text-sm">Users need to log some time entries first</p>
        </div>
      ) : (
        <div className="space-y-4">
          {analytics.map((activity) => (
            <div
              key={activity.id}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{
                      backgroundColor: activity.category?.color || "#3498db",
                    }}
                  ></div>
                  <div>
                    <h3 className="font-medium text-gray-800">
                      {activity.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {activity.category?.name}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg text-blue-600">
                    {activity.totalHours || 0}h
                  </p>
                  <p className="text-xs text-gray-500">
                    {activity.totalEntries || 0} entries •{" "}
                    {activity.uniqueUsers || 0} users
                  </p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-blue-500 transition-all"
                  style={{
                    width: `${Math.min(
                      ((activity.totalHours || 0) /
                        Math.max(
                          ...analytics.map((a) => a.totalHours || 0),
                          1
                        )) *
                        100,
                      100
                    )}%`,
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// School Years Component
const SchoolYearsPanel = ({ token, schoolId, onBack }) => {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [actionLoading, setActionLoading] = useState(false);

  const [showStartModal, setShowStartModal] = useState(false);
  const [startFromYear, setStartFromYear] = useState(null); // year being archived, or null for "create first year"
  const [newYearForm, setNewYearForm] = useState({ name: "", startDate: "", endDate: "" });

  const [editingYear, setEditingYear] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", startDate: "", endDate: "" });

  const [copyModal, setCopyModal] = useState(null); // { sourceYear, activities, selectedIds }

  const apiCall = async (url, options = {}) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || "http://localhost:3001"}${url}`,
      {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Request failed");
    }

    return response.json();
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 4000);
  };

  useEffect(() => {
    fetchYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const fetchYears = async () => {
    if (!schoolId) {
      setYears([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await apiCall(`/api/admin/school-years?schoolId=${schoolId}`);
      setYears(data);
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const currentYear = years.find((y) => y.isCurrent);

  const openStartModal = (fromYear) => {
    setStartFromYear(fromYear || null);
    setNewYearForm({ name: "", startDate: "", endDate: "" });
    setShowStartModal(true);
  };

  const submitStartModal = async () => {
    if (!newYearForm.name || !newYearForm.startDate || !newYearForm.endDate) {
      showMessage("error", "Please fill in name, start date, and end date");
      return;
    }
    setActionLoading(true);
    try {
      if (startFromYear) {
        await apiCall(`/api/admin/school-years/${startFromYear.id}/archive-and-start`, {
          method: "POST",
          body: JSON.stringify(newYearForm),
        });
        showMessage("success", `${startFromYear.name} archived. ${newYearForm.name} is now the current school year.`);
      } else {
        await apiCall(`/api/admin/school-years`, {
          method: "POST",
          body: JSON.stringify({ ...newYearForm, schoolId }),
        });
        showMessage("success", `${newYearForm.name} created`);
      }
      setShowStartModal(false);
      await fetchYears();
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const startEdit = (year) => {
    setEditingYear(year);
    setEditForm({
      name: year.name,
      startDate: year.startDate.slice(0, 10),
      endDate: year.endDate.slice(0, 10),
    });
  };

  const submitEdit = async () => {
    setActionLoading(true);
    try {
      await apiCall(`/api/admin/school-years/${editingYear.id}`, {
        method: "PUT",
        body: JSON.stringify(editForm),
      });
      showMessage("success", "School year updated");
      setEditingYear(null);
      await fetchYears();
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnarchive = async (year) => {
    if (!window.confirm(`Unarchive "${year.name}"? This makes its events and hours editable again until you re-archive it.`)) {
      return;
    }
    setActionLoading(true);
    try {
      await apiCall(`/api/admin/school-years/${year.id}/unarchive`, { method: "POST" });
      showMessage("success", `${year.name} unarchived`);
      await fetchYears();
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const openCopyModal = async (sourceYear) => {
    setActionLoading(true);
    try {
      const activities = await apiCall(`/api/admin/activities?schoolId=${schoolId}&schoolYearId=${sourceYear.id}`);
      setCopyModal({ sourceYear, activities, selectedIds: activities.map((a) => a.id) });
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const submitCopy = async () => {
    if (copyModal.selectedIds.length === 0) {
      showMessage("error", "Select at least one activity to copy");
      return;
    }
    setActionLoading(true);
    try {
      const result = await apiCall(`/api/admin/school-years/${copyModal.sourceYear.id}/copy-activities`, {
        method: "POST",
        body: JSON.stringify({ activityIds: copyModal.selectedIds }),
      });
      showMessage("success", result.message);
      setCopyModal(null);
      await fetchYears();
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-US", { timeZone: "UTC", year: "numeric", month: "short", day: "numeric" });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">School Years</h2>
        {onBack && (
          <button onClick={onBack} className="text-blue-600 hover:text-blue-800 px-3 py-1 rounded-lg hover:bg-blue-50">
            Back to Admin
          </button>
        )}
      </div>

      {message.text && (
        <div
          className={`rounded-lg p-3 flex items-center space-x-2 ${
            message.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {message.type === "success" ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {!schoolId ? (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">Please select a school to manage school years</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
        </div>
      ) : (
        <>
          <div className="flex justify-end">
            {currentYear ? (
              <button
                onClick={() => openStartModal(currentYear)}
                disabled={actionLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm disabled:opacity-50"
              >
                <Archive className="w-4 h-4" />
                <span>End "{currentYear.name}" & Start New Year</span>
              </button>
            ) : (
              <button
                onClick={() => openStartModal(null)}
                disabled={actionLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                <span>Create First School Year</span>
              </button>
            )}
          </div>

          {years.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No school years yet for this school</p>
              <p className="text-sm">Create the first one to start logging events and hours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {years.map((year) => (
                <div key={year.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-gray-800 flex items-center gap-2">
                          {year.name}
                          {year.isCurrent && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Current</span>
                          )}
                          {year.isArchived && (
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Archived
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {formatDate(year.startDate)} – {formatDate(year.endDate)}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {year._count?.activities || 0} events/activities
                          </span>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {year._count?.timeEntries || 0} hour entries
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-2 self-start sm:self-center">
                      {!year.isArchived && (
                        <button
                          onClick={() => startEdit(year)}
                          disabled={actionLoading}
                          className="text-blue-500 hover:text-blue-700 p-2 rounded hover:bg-blue-50 transition-colors disabled:opacity-50"
                          title="Edit dates/name"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {year.isArchived && (
                        <>
                          <button
                            onClick={() => openCopyModal(year)}
                            disabled={actionLoading}
                            className="text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded hover:bg-blue-50 transition-colors text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                            title="Copy activities into the current year"
                          >
                            <Copy className="w-3.5 h-3.5" /> Copy Activities Forward
                          </button>
                          <button
                            onClick={() => handleUnarchive(year)}
                            disabled={actionLoading}
                            className="text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded hover:bg-gray-100 transition-colors text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                            title="Unarchive to make corrections"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Unarchive
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Start / Archive-and-Start Modal */}
      {showStartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {startFromYear ? `End "${startFromYear.name}" & Start New Year` : "Create First School Year"}
              </h3>
              <button onClick={() => setShowStartModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {startFromYear && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                This will archive "{startFromYear.name}" — its events and hours become read-only. You can copy its activities into the new year afterward.
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year Name</label>
                <input
                  type="text"
                  value={newYearForm.name}
                  onChange={(e) => setNewYearForm({ ...newYearForm, name: e.target.value })}
                  placeholder="e.g. 2026-2027"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={newYearForm.startDate}
                    onChange={(e) => setNewYearForm({ ...newYearForm, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={newYearForm.endDate}
                    onChange={(e) => setNewYearForm({ ...newYearForm, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowStartModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={submitStartModal}
                disabled={actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {startFromYear ? "Archive & Start New Year" : "Create Year"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingYear && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Edit School Year</h3>
              <button onClick={() => setEditingYear(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={editForm.startDate}
                    onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={editForm.endDate}
                    onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditingYear(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={submitEdit}
                disabled={actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Activities Modal */}
      {copyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Copy Activities from "{copyModal.sourceYear.name}"
              </h3>
              <button onClick={() => setCopyModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {!currentYear ? (
              <p className="text-sm text-red-600">There is no current school year to copy into.</p>
            ) : copyModal.activities.length === 0 ? (
              <p className="text-sm text-gray-500">This year has no activities to copy.</p>
            ) : (
              <div className="overflow-y-auto flex-1 space-y-2 mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Select which activities to duplicate into "{currentYear.name}". Dates can be adjusted afterward.
                </p>
                {copyModal.activities.map((activity) => (
                  <label key={activity.id} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={copyModal.selectedIds.includes(activity.id)}
                      onChange={(e) => {
                        const selectedIds = e.target.checked
                          ? [...copyModal.selectedIds, activity.id]
                          : copyModal.selectedIds.filter((id) => id !== activity.id);
                        setCopyModal({ ...copyModal, selectedIds });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-800">{activity.name}</span>
                    <span className="text-xs text-gray-500">({activity.category?.name})</span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-2">
              <button onClick={() => setCopyModal(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              {currentYear && copyModal.activities.length > 0 && (
                <button
                  onClick={submitCopy}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy {copyModal.selectedIds.length} Activities
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPanel = ({ user, token, onBack }) => {
  // School Context
  const { schools: userSchools, selectedSchoolId, selectedSchool, selectSchool } = useSchool();

  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeEntriesSummary, setTimeEntriesSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [activitySuggestions, setActivitySuggestions] = useState([]);
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Admin school filter (separate from user's selected school)
  const [adminFilterSchoolId, setAdminFilterSchoolId] = useState("");

  // School year filter for Time Entries / Analytics tabs (shared across both)
  const [schoolYearOptions, setSchoolYearOptions] = useState([]);
  const [adminFilterYearId, setAdminFilterYearId] = useState("");

  // Users tab sort controls
  const [userSortBy, setUserSortBy] = useState("createdAt");
  const [userSortOrder, setUserSortOrder] = useState("desc");

  // Time entries filters
  const [timeEntryFilters, setTimeEntryFilters] = useState({
    startDate: "",
    endDate: "",
    userId: "",
    activityId: "",
  });

  // Sorting state
  const [timeEntrySortBy, setTimeEntrySortBy] = useState("entryDate");
  const [timeEntrySortOrder, setTimeEntrySortOrder] = useState("desc");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Form states
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    role: "user",
    isActive: true,
    schoolIds: [],
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    color: "#3498db",
    schoolId: "",
  });

  const [schoolForm, setSchoolForm] = useState({
    name: "",
    logoUrl: "",
  });

  const [schoolLogoFile, setSchoolLogoFile] = useState(null);

  // Updated activityForm with new fields
  const [activityForm, setActivityForm] = useState({
    name: "",
    description: "",
    categoryId: "",
    schoolId: "",
    isActive: true,
    activeFrom: "",
    activeUntil: "",
    requiresDescription: false,
    descriptionPrompt: "",
    isYearLong: false, // Add this new field
  });

  const apiCall = async (url, options = {}) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || "http://localhost:3001"}${url}`,
      {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Request failed");
    }

    return response.json();
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 3000);
  };

  useEffect(() => {
    if (user?.role === "admin") {
      fetchSuggestionCount();
    }
  }, [user]);

  useEffect(() => {
    fetchData();
    setShowModal(false);
    setEditingItem(null);
    setSearchTerm("");
  }, [activeTab, adminFilterSchoolId]);

  useEffect(() => {
    if (activeTab === "users") {
      fetchData();
    }
  }, [userSortBy, userSortOrder]);

  useEffect(() => {
    if (activeTab === "timeentries") {
      const debounceTimer = setTimeout(() => {
        fetchTimeEntries();
        fetchTimeEntriesSummary();
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
  }, [timeEntryFilters, timeEntrySortBy, timeEntrySortOrder, activeTab, adminFilterYearId]);

  // Load the selected school's years whenever it changes, and default the year filter to its current year
  useEffect(() => {
    if (!adminFilterSchoolId) {
      setSchoolYearOptions([]);
      setAdminFilterYearId("");
      return;
    }
    (async () => {
      try {
        const years = await apiCall(`/api/admin/school-years?schoolId=${adminFilterSchoolId}`);
        setSchoolYearOptions(years || []);
        const current = (years || []).find((y) => y.isCurrent);
        setAdminFilterYearId(current ? current.id : "");
      } catch (error) {
        setSchoolYearOptions([]);
        setAdminFilterYearId("");
      }
    })();
  }, [adminFilterSchoolId]);

  const fetchData = async () => {
    // Don't fetch if school filter is required but not selected
    if (!adminFilterSchoolId && activeTab !== "users" && activeTab !== "schools") {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      switch (activeTab) {
        case "users":
          const userParams = new URLSearchParams({ limit: "1000", sortBy: userSortBy, sortOrder: userSortOrder });
          if (adminFilterSchoolId) userParams.append("schoolId", adminFilterSchoolId);
          const usersData = await apiCall(`/api/admin/users?${userParams}`);
          setUsers(usersData.users || []);
          // Also fetch schools if not loaded (needed for user form)
          if (schools.length === 0) {
            const schoolsData = await apiCall("/api/admin/schools");
            setSchools(schoolsData || []);
          }
          break;
        case "schools":
          const schoolsData = await apiCall("/api/admin/schools");
          setSchools(schoolsData || []);
          break;
        case "schoolyears":
          if (schools.length === 0) {
            const schoolsForYears = await apiCall("/api/admin/schools");
            setSchools(schoolsForYears || []);
          }
          break;
        case "categories":
          const categoriesUrl = adminFilterSchoolId
            ? `/api/admin/categories?schoolId=${adminFilterSchoolId}`
            : "/api/admin/categories";
          const categoriesData = await apiCall(categoriesUrl);
          setCategories(categoriesData);
          break;
        case "activities":
          const activitiesUrl = adminFilterSchoolId
            ? `/api/admin/activities?schoolId=${adminFilterSchoolId}`
            : "/api/admin/activities";
          const activitiesData = await apiCall(activitiesUrl);
          setActivities(activitiesData);
          if (categories.length === 0) {
            const catUrl = adminFilterSchoolId
              ? `/api/admin/categories?schoolId=${adminFilterSchoolId}`
              : "/api/admin/categories";
            const catData = await apiCall(catUrl);
            setCategories(catData);
          }
          break;
        case "suggestions":
          await fetchActivitySuggestions();
          // Also fetch categories if not already loaded
          if (categories.length === 0 && adminFilterSchoolId) {
            const catData = await apiCall(`/api/admin/categories?schoolId=${adminFilterSchoolId}`);
            setCategories(catData);
          }
          break;
        case "timeentries":
          await fetchTimeEntries();
          await fetchTimeEntriesSummary();
          if (users.length === 0) {
            const usersData = await apiCall("/api/admin/users?limit=1000");
            setUsers(usersData.users || []);
          }
          if (activities.length === 0 && adminFilterSchoolId) {
            const activitiesData = await apiCall(`/api/admin/activities?schoolId=${adminFilterSchoolId}`);
            setActivities(activitiesData);
          }
          break;
        case "photosync":
          // No initial data fetch needed for photo sync
          break;
        default:
          break;
      }
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const params = new URLSearchParams();
      if (timeEntryFilters.startDate)
        params.append("startDate", timeEntryFilters.startDate);
      if (timeEntryFilters.endDate)
        params.append("endDate", timeEntryFilters.endDate);
      if (timeEntryFilters.userId)
        params.append("userId", timeEntryFilters.userId);
      if (timeEntryFilters.activityId)
        params.append("activityId", timeEntryFilters.activityId);
      if (adminFilterSchoolId)
        params.append("schoolId", adminFilterSchoolId);
      if (adminFilterYearId)
        params.append("schoolYearId", adminFilterYearId);

      // Add sorting parameters
      params.append("sortBy", timeEntrySortBy);
      params.append("sortOrder", timeEntrySortOrder);

      const data = await apiCall(`/api/admin/time-entries?${params}`);
      setTimeEntries(data.timeEntries || []);
    } catch (error) {
      showMessage("error", "Failed to fetch time entries");
    }
  };

  const fetchTimeEntriesSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (timeEntryFilters.startDate)
        params.append("startDate", timeEntryFilters.startDate);
      if (timeEntryFilters.endDate)
        params.append("endDate", timeEntryFilters.endDate);
      if (adminFilterSchoolId)
        params.append("schoolId", adminFilterSchoolId);
      if (adminFilterYearId)
        params.append("schoolYearId", adminFilterYearId);

      const data = await apiCall(`/api/admin/time-entries/summary?${params}`);
      setTimeEntriesSummary(data);
    } catch (error) {
      console.error("Failed to fetch time entries summary:", error);
    }
  };

  const handleTimeEntryDelete = async (entryId, entryDescription) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this time entry?\n\n${entryDescription}`
      )
    ) {
      return;
    }

    try {
      await apiCall(`/api/admin/time-entries/${entryId}`, {
        method: "DELETE",
      });

      await fetchTimeEntries();
      await fetchTimeEntriesSummary();
      showMessage("success", "Time entry deleted successfully");
    } catch (error) {
      showMessage("error", error.message);
    }
  };

  // CRUD handlers for users
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      // Validate at least one school is selected
      if (!userForm.schoolIds || userForm.schoolIds.length === 0) {
        showMessage("error", "Please select at least one school for the user");
        setModalLoading(false);
        return;
      }

      await apiCall("/api/admin/users", {
        method: "POST",
        body: JSON.stringify(userForm),
      });
      setShowModal(false);
      resetUserForm();
      fetchData();
      showMessage("success", "User created successfully");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      const updateData = { ...userForm };
      // Only include password if it's provided
      if (!updateData.password) {
        delete updateData.password;
      }

      await apiCall(`/api/admin/users/${editingItem.id}`, {
        method: "PUT",
        body: JSON.stringify(updateData),
      });
      setShowModal(false);
      setEditingItem(null);
      resetUserForm();
      fetchData();
      showMessage("success", "User updated successfully");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // CRUD handlers for categories
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      await apiCall("/api/admin/categories", {
        method: "POST",
        body: JSON.stringify(categoryForm),
      });
      setShowModal(false);
      resetCategoryForm();
      fetchData();
      showMessage("success", "Category created successfully");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      await apiCall(`/api/admin/categories/${editingItem.id}`, {
        method: "PUT",
        body: JSON.stringify(categoryForm),
      });
      setShowModal(false);
      setEditingItem(null);
      resetCategoryForm();
      fetchData();
      showMessage("success", "Category updated successfully");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // CRUD handlers for schools
  const handleCreateSchool = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      // First create the school
      const response = await apiCall("/api/admin/schools", {
        method: "POST",
        body: JSON.stringify({ name: schoolForm.name }),
      });

      // If there's a logo file, upload it
      if (schoolLogoFile && response.id) {
        const formData = new FormData();
        formData.append('logo', schoolLogoFile);

        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/schools/${response.id}/logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
      }

      setShowModal(false);
      resetSchoolForm();
      setSchoolLogoFile(null);
      fetchData();
      showMessage("success", "School created successfully");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateSchool = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      // Update the school name
      await apiCall(`/api/admin/schools/${editingItem.id}`, {
        method: "PUT",
        body: JSON.stringify({ name: schoolForm.name }),
      });

      // If there's a new logo file, upload it
      if (schoolLogoFile) {
        const formData = new FormData();
        formData.append('logo', schoolLogoFile);

        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/schools/${editingItem.id}/logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
      }

      setShowModal(false);
      setEditingItem(null);
      resetSchoolForm();
      setSchoolLogoFile(null);
      fetchData();
      showMessage("success", "School updated successfully");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // CRUD handlers for activities
  const handleCreateActivity = async (e) => {
    e.preventDefault();
    setModalLoading(true);

    try {
      const activityData = { ...activityForm };

      // If it's a year-long activity, clear the date fields
      if (activityData.isYearLong) {
        activityData.activeFrom = null;
        activityData.activeUntil = null;
      } else {
        // Convert empty dates to null for non-year-long activities
        if (!activityData.activeFrom) activityData.activeFrom = null;
        if (!activityData.activeUntil) activityData.activeUntil = null;
      }

      // Remove the isYearLong field before sending to API
      delete activityData.isYearLong;

      // Check if this is approving a suggestion
      if (editingItem?.type === "suggestion") {
        console.log("Approving suggestion with ID:", editingItem.id);

        // Approve the suggestion with the activity data
        await apiCall(
          `/api/admin/activity-suggestions/${editingItem.id}/approve`,
          {
            method: "POST",
            body: JSON.stringify(activityData),
          }
        );
        showMessage(
          "success",
          "Activity suggestion approved and activity created successfully"
        );

        // Refresh suggestions data
        await fetchActivitySuggestions();
        await fetchSuggestionCount();
      } else if (editingItem?.id && !editingItem?.type) {
        // Regular activity update (existing activity)
        await apiCall(`/api/admin/activities/${editingItem.id}`, {
          method: "PUT",
          body: JSON.stringify(activityData),
        });
        showMessage("success", "Activity updated successfully");
      } else {
        // Regular activity creation (new activity)
        await apiCall("/api/admin/activities", {
          method: "POST",
          body: JSON.stringify(activityData),
        });
        showMessage("success", "Activity created successfully");
      }

      setShowModal(false);
      resetActivityForm();
      setEditingItem(null);
      fetchData(); // This will refresh the current tab
    } catch (error) {
      console.error("Activity creation/update error:", error);
      showMessage("error", error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateActivity = async (e) => {
    e.preventDefault();
    setModalLoading(true);

    try {
      const activityData = { ...activityForm };

      // If it's a year-long activity, clear the date fields
      if (activityData.isYearLong) {
        activityData.activeFrom = null;
        activityData.activeUntil = null;
      } else {
        // Convert empty dates to null for non-year-long activities
        if (!activityData.activeFrom) activityData.activeFrom = null;
        if (!activityData.activeUntil) activityData.activeUntil = null;
      }

      // Remove the isYearLong field before sending to API
      delete activityData.isYearLong;

      // This should only be called for regular activity updates, not suggestions
      if (editingItem?.type === "suggestion") {
        throw new Error("Suggestion approval should use handleCreateActivity");
      }

      await apiCall(`/api/admin/activities/${editingItem.id}`, {
        method: "PUT",
        body: JSON.stringify(activityData),
      });
      setShowModal(false);
      setEditingItem(null);
      resetActivityForm();
      fetchData();
      showMessage("success", "Activity updated successfully");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (type, id, name) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await apiCall(`/api/admin/${type}/${id}`, {
        method: "DELETE",
      });
      fetchData();
      showMessage(
        "success",
        `${
          type.slice(0, -1).charAt(0).toUpperCase() + type.slice(1, -1)
        } deleted successfully`
      );
    } catch (error) {
      showMessage("error", error.message);
    }
  };

  const resetUserForm = () => {
    setUserForm({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      role: "user",
      isActive: true,
      schoolIds: [],
    });
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: "",
      description: "",
      color: "#3498db",
      schoolId: "",
    });
  };

  const resetSchoolForm = () => {
    setSchoolForm({
      name: "",
      logoUrl: "",
    });
    setSchoolLogoFile(null);
  };

  // Updated resetActivityForm with new fields
  const resetActivityForm = () => {
    setActivityForm({
      name: "",
      description: "",
      categoryId: "",
      schoolId: "",
      isActive: true,
      activeFrom: "",
      activeUntil: "",
      requiresDescription: false,
      descriptionPrompt: "",
      isYearLong: false, // Add this new field
    });
  };

  const openModal = async (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowPassword(false);

    // Fetch schools if opening user, category, or activity modal and schools not loaded
    if ((type === "user" || type === "category" || type === "activity") && schools.length === 0) {
      try {
        const schoolsData = await apiCall("/api/admin/schools");
        setSchools(schoolsData || []);
      } catch (error) {
        showMessage("error", "Failed to load schools");
      }
    }

    if (item) {
      if (type === "user") {
        setUserForm({
          email: item.email,
          password: "",
          firstName: item.firstName,
          lastName: item.lastName,
          role: item.role,
          isActive: item.isActive,
          schoolIds: item.userSchools ? item.userSchools.map(us => us.school.id) : [],
        });
      } else if (type === "category") {
        setCategoryForm({
          name: item.name,
          description: item.description || "",
          color: item.color,
          schoolId: item.schoolId || "",
        });
      } else if (type === "school") {
        setSchoolForm({
          name: item.name,
          logoUrl: item.logoUrl || "",
        });
      } else if (type === "activity") {
        // Determine if activity is year-long (no activeFrom date)
        const isYearLong = !item.activeFrom;

        setActivityForm({
          name: item.name,
          description: item.description || "",
          categoryId: item.categoryId,
          schoolId: item.schoolId || "",
          isActive: item.isActive,
          activeFrom: item.activeFrom ? item.activeFrom.split("T")[0] : "",
          activeUntil: item.activeUntil ? item.activeUntil.split("T")[0] : "",
          requiresDescription: item.requiresDescription || false,
          descriptionPrompt: item.descriptionPrompt || "",
          isYearLong: isYearLong, // Set based on whether activeFrom exists
        });
      }
    } else {
      if (type === "user") resetUserForm();
      else if (type === "category") resetCategoryForm();
      else if (type === "school") resetSchoolForm();
      else if (type === "activity") resetActivityForm();
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setModalType("");
    setModalLoading(false);
    setShowPassword(false);
    setSchoolLogoFile(null);
  };

  const filteredData = () => {
    if (activeTab === "users") {
      return users.filter(
        (user) =>
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.lastName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else if (activeTab === "schools") {
      return schools.filter((school) =>
        school.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else if (activeTab === "categories") {
      return categories.filter((cat) =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else if (activeTab === "activities") {
      return activities.filter(
        (act) =>
          act.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          act.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return [];
  };

  const fetchActivitySuggestions = async () => {
    try {
      setLoading(true);
      const url = adminFilterSchoolId
        ? `/api/admin/activity-suggestions?schoolId=${adminFilterSchoolId}`
        : "/api/admin/activity-suggestions";
      const data = await apiCall(url);
      setActivitySuggestions(data.suggestions);
    } catch (error) {
      showMessage("error", "Failed to load activity suggestions");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestionCount = async () => {
    try {
      const data = await apiCall("/api/admin/activity-suggestions/count");
      setSuggestionCount(data.pendingCount);
    } catch (error) {
      console.error("Failed to fetch suggestion count:", error);
    }
  };

  const handleApproveSuggestion = async (suggestion) => {
    // Determine schoolId from the suggestion's category or use the first school if available
    const schoolId = suggestion.category?.schoolId ||
                     (userSchools.length > 0 ? userSchools[0].id : "");

    setActivityForm({
      name: suggestion.name,
      description: suggestion.description || "",
      schoolId: schoolId,
      categoryId: suggestion.categoryId || "",
      isActive: true,
      activeFrom: suggestion.activeFrom
        ? suggestion.activeFrom.split("T")[0]
        : "",
      activeUntil: suggestion.activeUntil
        ? suggestion.activeUntil.split("T")[0]
        : "",
      requiresDescription: false,
      descriptionPrompt: "",
      isYearLong: suggestion.isYearLong || false,
    });

    // Fetch categories for the determined school
    if (schoolId) {
      try {
        const catData = await apiCall(`/api/admin/categories?schoolId=${schoolId}`);
        setCategories(catData);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    }

    setEditingItem({ id: suggestion.id, type: "suggestion" });
    setModalType("activity");
    setShowModal(true);
  };

  const handleRejectSuggestion = async (suggestion) => {
    setSelectedSuggestion(suggestion);
    setActionReason("");
    setShowRejectModal(true);
  };

  const handleDeleteSuggestion = async (suggestion) => {
    setSelectedSuggestion(suggestion);
    setActionReason("");
    setShowDeleteModal(true);
  };

  // Rejection confirmation:
  const confirmRejectSuggestion = async () => {
    if (!selectedSuggestion) return;

    setActionLoading(true);
    try {
      await apiCall(
        `/api/admin/activity-suggestions/${selectedSuggestion.id}/reject`,
        {
          method: "POST",
          body: JSON.stringify({ reason: actionReason.trim() || null }),
        }
      );

      await fetchActivitySuggestions();
      await fetchSuggestionCount();
      showMessage("success", "Activity suggestion rejected");
      setShowRejectModal(false);
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Deletion confirmation:
  const confirmDeleteSuggestion = async () => {
    if (!selectedSuggestion) return;

    setActionLoading(true);
    try {
      await apiCall(
        `/api/admin/activity-suggestions/${selectedSuggestion.id}`,
        {
          method: "DELETE",
          body: JSON.stringify({ reason: actionReason.trim() || null }),
        }
      );

      await fetchActivitySuggestions();
      await fetchSuggestionCount();
      showMessage(
        "success",
        actionReason.trim()
          ? "Activity suggestion marked as deleted with reason"
          : "Activity suggestion deleted permanently"
      );
      setShowDeleteModal(false);
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Close modals:
  const closeActionModals = () => {
    setShowRejectModal(false);
    setShowDeleteModal(false);
    setSelectedSuggestion(null);
    setActionReason("");
    setActionLoading(false);
  };

  // Render Analytics View
  if (activeTab === "analytics") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={onBack}
                  className="text-blue-600 hover:text-blue-800 p-1 sm:p-2 rounded-lg hover:bg-blue-50"
                  title="Back to Time Tracker"
                >
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                <EsusdLogo
                  size={160}
                  className="sm:w-28 sm:h-28"
                />
                <div>
                  <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 flex items-center">
                    <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 mr-2 sm:mr-3 text-blue-600" />
                    Admin Panel - Analytics
                  </h1>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600">
                    View activity time tracking
                    statistics
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* School Filter */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="flex items-center text-sm font-medium text-gray-700 whitespace-nowrap">
                <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                Filter by School:
              </label>
              <select
                value={adminFilterSchoolId}
                onChange={(e) => setAdminFilterSchoolId(e.target.value)}
                className="flex-1 sm:flex-none sm:min-w-[250px] px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select a school to view analytics...</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* School Year Filter */}
          {schoolYearOptions.length > 0 && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label className="flex items-center text-sm font-medium text-gray-700 whitespace-nowrap">
                  <Calendar className="w-4 h-4 mr-2 text-indigo-600" />
                  School Year:
                </label>
                <select
                  value={adminFilterYearId}
                  onChange={(e) => setAdminFilterYearId(e.target.value)}
                  className="flex-1 sm:flex-none sm:min-w-[220px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  {schoolYearOptions.map((year) => (
                    <option key={year.id} value={year.id}>
                      {year.name}{year.isCurrent ? " (Current)" : ""}{year.isArchived ? " (Archived)" : ""}
                    </option>
                  ))}
                  <option value="all">All Years</option>
                </select>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <SimpleAnalytics
              user={user}
              token={token}
              onBack={() => setActiveTab("users")}
              schoolId={adminFilterSchoolId}
              schoolYearId={adminFilterYearId}
            />
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === "schoolyears") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={onBack}
                  className="text-blue-600 hover:text-blue-800 p-1 sm:p-2 rounded-lg hover:bg-blue-50"
                  title="Back to Time Tracker"
                >
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                <EsusdLogo size={160} className="sm:w-28 sm:h-28" />
                <div>
                  <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 flex items-center">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 mr-2 sm:mr-3 text-blue-600" />
                    Admin Panel - School Years
                  </h1>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600">
                    Archive a finished year, start a new one, and review past years
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* School Filter */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="flex items-center text-sm font-medium text-gray-700 whitespace-nowrap">
                <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                Filter by School:
              </label>
              <select
                value={adminFilterSchoolId}
                onChange={(e) => setAdminFilterSchoolId(e.target.value)}
                className="flex-1 sm:flex-none sm:min-w-[250px] px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select a school to manage school years...</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <SchoolYearsPanel
              token={token}
              schoolId={adminFilterSchoolId}
              onBack={() => setActiveTab("users")}
            />
          </div>
        </div>
      </div>
    );
  }

  // Main Admin Panel Render
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 md:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={onBack}
                className="text-blue-600 hover:text-blue-800 p-1 sm:p-2 rounded-lg hover:bg-blue-50 flex-shrink-0"
                title="Back to Time Tracker"
              >
                <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <EsusdLogo
                size={160}
                className="flex-shrink-0 h-32 sm:h-40 md:h-48 w-auto"
              />
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-lg md:text-2xl lg:text-3xl font-bold text-gray-800 mb-1 leading-tight flex items-center">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 mr-2 text-blue-600 flex-shrink-0" />
                  <span>Admin Panel</span>
                </h1>
                <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-tight">
                  Manage users, categories, and activities
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div
            className={`rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 flex items-center space-x-2 ${
              message.type === "success"
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            )}
            <span className="text-sm sm:text-base">{message.text}</span>
          </div>
        )}

        {/* Main Panel */}
        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 md:p-6">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-4 sm:mb-6 overflow-x-auto pb-2 thin-scrollbar">
            <div className="flex space-x-0.5 sm:space-x-1 md:space-x-2 min-w-max">
              <button
                onClick={() => setActiveTab("users")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "users"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                Users
              </button>

              <button
                onClick={() => setActiveTab("schools")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "schools"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Building2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                Schools
              </button>

              <button
                onClick={() => setActiveTab("categories")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "categories"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <FolderOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                Categories
              </button>

              <button
                onClick={() => setActiveTab("activities")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "activities"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Settings className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                Activities
              </button>

              <button
                onClick={() => setActiveTab("schoolyears")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "schoolyears"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                <span className="hidden sm:inline">School Years</span>
                <span className="sm:hidden">Years</span>
              </button>

              <button
                onClick={() => setActiveTab("suggestions")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "suggestions"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                <span className="hidden sm:inline">Suggestions</span>
                <span className="sm:hidden">Suggest</span>
                {suggestionCount > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                    {suggestionCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab("timeentries")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "timeentries"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                <span className="hidden xs:inline">Time Entries</span>
                <span className="xs:hidden">Time</span>
              </button>

              <button
                onClick={() => setActiveTab("photosync")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "photosync"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <FolderOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                <span className="hidden sm:inline">Photo Sync</span>
                <span className="sm:hidden">Photos</span>
              </button>

              <button
                onClick={() => setActiveTab("analytics")}
                className={`flex items-center px-1.5 sm:px-2 md:px-3 py-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === "analytics"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                Analytics
              </button>
            </div>
          </div>

          {/* School Filter - Show for all tabs except Schools */}
          {activeTab !== "schools" && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label className="flex items-center text-sm font-medium text-gray-700 whitespace-nowrap">
                  <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                  Filter by School:
                </label>
                <select
                  value={adminFilterSchoolId}
                  onChange={(e) => setAdminFilterSchoolId(e.target.value)}
                  className="flex-1 sm:flex-none sm:min-w-[250px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">{activeTab === "users" ? "All Schools" : "Select a school to view data..."}</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name}
                    </option>
                  ))}
                </select>
                {adminFilterSchoolId && (
                  <span className="text-xs text-gray-600 bg-white px-2 py-1 rounded border border-gray-200">
                    Showing data for selected school only
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Show message if school not selected */}
          {activeTab !== "users" && activeTab !== "schools" && !adminFilterSchoolId && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Please select a school
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Select a school from the dropdown above to view {activeTab === "categories" ? "categories" : activeTab === "activities" ? "activities" : activeTab === "suggestions" ? "suggestions" : activeTab === "timeentries" ? "time entries" : activeTab === "analytics" ? "analytics" : "data"} for that school.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Photo Sync Content */}
          {activeTab === "photosync" && <GoogleDriveSyncPanel token={token} schoolId={adminFilterSchoolId} />}

          {/* Time Entries Content */}
          {activeTab === "timeentries" && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">
                Time Entries Management
              </h3>

              {/* School Year selector */}
              {schoolYearOptions.length > 0 && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="flex items-center text-sm font-medium text-gray-700 whitespace-nowrap">
                      <Calendar className="w-4 h-4 mr-2 text-indigo-600" />
                      School Year:
                    </label>
                    <select
                      value={adminFilterYearId}
                      onChange={(e) => setAdminFilterYearId(e.target.value)}
                      className="flex-1 sm:flex-none sm:min-w-[220px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      {schoolYearOptions.map((year) => (
                        <option key={year.id} value={year.id}>
                          {year.name}{year.isCurrent ? " (Current)" : ""}{year.isArchived ? " (Archived)" : ""}
                        </option>
                      ))}
                      <option value="all">All Years</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3 md:p-4">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mr-1 sm:mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-blue-600">
                        Total Entries
                      </p>
                      <p className="text-base sm:text-xl font-bold text-blue-700">
                        {timeEntriesSummary.totalEntries || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 md:p-4">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-1 sm:mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-green-600">
                        Total Hours
                      </p>
                      <p className="text-base sm:text-xl font-bold text-green-700">
                        {timeEntriesSummary.totalHours || 0}h
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2 sm:p-3 md:p-4">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mr-1 sm:mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-purple-600">
                        Active Users
                      </p>
                      <p className="text-base sm:text-xl font-bold text-purple-700">
                        {timeEntriesSummary.uniqueUsers || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-2 sm:p-3 md:p-4">
                  <div className="flex items-center">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mr-1 sm:mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-orange-600">
                        Avg Hours/Entry
                      </p>
                      <p className="text-base sm:text-xl font-bold text-orange-700">
                        {timeEntriesSummary.averageHoursPerEntry || 0}h
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <h4 className="text-sm sm:text-base font-medium text-gray-800 mb-3">
                  Filter Time Entries
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={timeEntryFilters.startDate}
                      onChange={(e) =>
                        setTimeEntryFilters({
                          ...timeEntryFilters,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={timeEntryFilters.endDate}
                      onChange={(e) =>
                        setTimeEntryFilters({
                          ...timeEntryFilters,
                          endDate: e.target.value,
                        })
                      }
                      className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                      min={timeEntryFilters.startDate}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      User
                    </label>
                    <select
                      value={timeEntryFilters.userId}
                      onChange={(e) =>
                        setTimeEntryFilters({
                          ...timeEntryFilters,
                          userId: e.target.value,
                        })
                      }
                      className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    >
                      <option value="">All Users</option>
                      {[...users].sort((a, b) => {
                        const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
                        const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
                        return nameA.localeCompare(nameB);
                      }).map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.firstName} {user.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Activity
                    </label>
                    <select
                      value={timeEntryFilters.activityId}
                      onChange={(e) =>
                        setTimeEntryFilters({
                          ...timeEntryFilters,
                          activityId: e.target.value,
                        })
                      }
                      className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    >
                      <option value="">All Activities</option>
                      {[...activities].sort((a, b) => {
                        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                      }).map((activity) => (
                        <option key={activity.id} value={activity.id}>
                          {activity.name} ({activity.category?.name})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sorting Controls */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm sm:text-base font-medium text-gray-800 mb-3">
                    Sort By
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Sort Field
                      </label>
                      <select
                        value={timeEntrySortBy}
                        onChange={(e) => setTimeEntrySortBy(e.target.value)}
                        className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                      >
                        <option value="entryDate">Entry Date</option>
                        <option value="userName">User Name</option>
                        <option value="activityName">Activity Name</option>
                        <option value="hours">Hours</option>
                        <option value="activityDate">Activity Date</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Sort Order
                      </label>
                      <select
                        value={timeEntrySortOrder}
                        onChange={(e) => setTimeEntrySortOrder(e.target.value)}
                        className="w-full px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                      >
                        <option value="desc">Descending (Newest First)</option>
                        <option value="asc">Ascending (Oldest First)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => {
                      setTimeEntryFilters({
                        startDate: "",
                        endDate: "",
                        userId: "",
                        activityId: "",
                      });
                      setTimeEntrySortBy("entryDate");
                      setTimeEntrySortOrder("desc");
                    }}
                    className="px-3 py-1 sm:py-2 text-xs sm:text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Reset All
                  </button>
                </div>
              </div>

              {/* Time Entries List */}
              {loading ? (
                <div className="flex justify-center items-center py-8 sm:py-12">
                  <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-2 border-blue-600 border-t-transparent"></div>
                </div>
              ) : timeEntries.length === 0 ? (
                <div className="text-center py-8 sm:py-12 text-gray-500">
                  <Calendar className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm sm:text-base">No time entries found</p>
                  <p className="text-xs sm:text-sm">
                    Try adjusting your filters or check if users have logged
                    time
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {timeEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start space-x-2 sm:space-x-3 mb-2">
                            <div
                              className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 mt-1"
                              style={{
                                backgroundColor: entry.activity.category.color,
                              }}
                            ></div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-medium text-gray-800 text-sm sm:text-base truncate">
                                {entry.activity.name}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-500">
                                {entry.activity.category.name}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-2">
                            <span className="flex items-center">
                              <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                {entry.user.firstName} {entry.user.lastName}
                              </span>
                            </span>
                            <span className="flex items-center">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                              {new Date(entry.entryDate).toLocaleDateString(
                                "en-US",
                                { timeZone: "UTC" }
                              )}
                            </span>
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                              {entry.hours}h
                            </span>
                          </div>

                          {entry.notes && (
                            <p className="text-xs sm:text-sm text-gray-600 bg-gray-50 rounded p-2 mt-2 break-words">
                              {entry.notes}
                            </p>
                          )}

                          <div className="text-xs text-gray-400 mt-2">
                            Created:{" "}
                            {new Date(entry.createdAt).toLocaleString()}
                          </div>
                        </div>

                        <button
                          onClick={() =>
                            handleTimeEntryDelete(
                              entry.id,
                              `${entry.hours}h for ${entry.activity.name} by ${
                                entry.user.firstName
                              } ${entry.user.lastName} on ${new Date(
                                entry.entryDate
                              ).toLocaleDateString("en-US", {
                                timeZone: "UTC",
                              })}`
                            )
                          }
                          className="text-red-500 hover:text-red-700 p-2 rounded hover:bg-red-50 transition-colors flex-shrink-0 self-start"
                          title="Delete time entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Other Tabs Content - Users, Categories, Activities */}
          {activeTab !== "timeentries" &&
            activeTab !== "analytics" &&
            activeTab !== "photosync" && (
              <>
                {/* Search and Add Button */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-4 sm:mb-6">
                  <div className="relative flex-1 max-w-full sm:max-w-md">
                    <input
                      type="text"
                      placeholder={`Search ${activeTab}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <Search className="absolute left-2 sm:left-3 top-2.5 w-4 h-4 text-gray-400" />
                  </div>
                  {activeTab === "users" && (
                    <div className="flex items-center gap-2">
                      <select
                        value={userSortBy}
                        onChange={(e) => setUserSortBy(e.target.value)}
                        className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="name">Sort: Name</option>
                        <option value="createdAt">Sort: Date Created</option>
                        <option value="updatedAt">Sort: Date Updated</option>
                        <option value="lastTimeEntry">Sort: Last Time Entry</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => setUserSortOrder(userSortOrder === "asc" ? "desc" : "asc")}
                        className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                        title={userSortOrder === "asc" ? "Ascending" : "Descending"}
                      >
                        {userSortOrder === "asc" ? "↑" : "↓"}
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() =>
                      openModal(
                        activeTab === "users"
                          ? "user"
                          : activeTab === "schools"
                          ? "school"
                          : activeTab === "categories"
                          ? "category"
                          : "activity"
                      )
                    }
                    className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm w-full sm:w-auto"
                  >
                    <Plus className="w-4 h-4" />
                    <span>ADD {activeTab.toUpperCase()}</span>
                  </button>
                </div>

                {/* Content for Users, Categories, Activities */}
                {loading ? (
                  <div className="flex justify-center items-center py-8 sm:py-12">
                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-2 border-blue-600 border-t-transparent"></div>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {filteredData().map((item) => (
                      <div
                        key={item.id}
                        className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
                      >
                        {activeTab === "users" && (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                {item.role === "admin" ? (
                                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                ) : (
                                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-medium text-gray-800 text-sm sm:text-base truncate">
                                  {item.firstName} {item.lastName}
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-500 truncate">
                                  {item.email}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <div
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      item.role === "admin"
                                        ? "bg-purple-100 text-purple-700"
                                        : "bg-gray-100 text-gray-700"
                                    }`}
                                  >
                                    {item.role}
                                  </div>
                                  <div
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      item.isActive
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {item.isActive ? "Active" : "Inactive"}
                                  </div>
                                  {item.userSchools && item.userSchools.length > 0 && (
                                    <div className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium flex items-center space-x-1">
                                      <Building2 className="w-3 h-3" />
                                      <span>{item.userSchools.length} school{item.userSchools.length !== 1 ? 's' : ''}</span>
                                    </div>
                                  )}
                                  {(!item.userSchools || item.userSchools.length === 0) && (
                                    <div className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs font-medium">
                                      No schools
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-400">
                                  <span>Created {new Date(item.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" })}</span>
                                  {item.updatedAt && (
                                    <span>Updated {new Date(item.updatedAt).toLocaleDateString("en-US", { timeZone: "UTC" })}</span>
                                  )}
                                  <span>
                                    Last time entry{" "}
                                    {item.lastTimeEntryDate
                                      ? new Date(item.lastTimeEntryDate).toLocaleDateString("en-US", { timeZone: "UTC" })
                                      : "never"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 self-start sm:self-center">
                              <button
                                onClick={() => openModal("user", item)}
                                className="text-blue-500 hover:text-blue-700 p-1 sm:p-2 rounded hover:bg-blue-50 transition-colors"
                                title="Edit user"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {item.id !== user.id && (
                                <button
                                  onClick={() =>
                                    handleDelete("users", item.id, item.email)
                                  }
                                  className="text-red-500 hover:text-red-700 p-1 sm:p-2 rounded hover:bg-red-50 transition-colors"
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {activeTab === "schools" && (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              {/* Show school logo if available, otherwise show icon */}
                              {item.logoUrl ? (
                                <img
                                  src={item.logoUrl}
                                  alt={`${item.name} logo`}
                                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <h3 className="font-medium text-gray-800 text-sm sm:text-base">
                                  {item.name}
                                </h3>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <div className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs inline-block">
                                    {item._count?.userSchools || 0} users
                                  </div>
                                  <div className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs inline-block">
                                    {item._count?.activityCategories || 0} categories
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 self-start sm:self-center">
                              <button
                                onClick={() => openModal("school", item)}
                                className="text-blue-500 hover:text-blue-700 p-1 sm:p-2 rounded hover:bg-blue-50 transition-colors"
                                title="Edit school"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDelete("schools", item.id, item.name)
                                }
                                className="text-red-500 hover:text-red-700 p-1 sm:p-2 rounded hover:bg-red-50 transition-colors"
                                title="Delete school"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}

                        {activeTab === "categories" && (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              <div
                                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex-shrink-0 mt-1"
                                style={{ backgroundColor: item.color }}
                              ></div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-medium text-gray-800 text-sm sm:text-base">
                                  {item.name}
                                </h3>
                                {item.description && (
                                  <p className="text-xs sm:text-sm text-gray-500 mt-1 break-words">
                                    {item.description}
                                  </p>
                                )}
                                <div className="mt-2">
                                  <div className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs inline-block">
                                    {item._count?.activities || 0} activities
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 self-start sm:self-center">
                              <button
                                onClick={() => openModal("category", item)}
                                className="text-blue-500 hover:text-blue-700 p-1 sm:p-2 rounded hover:bg-blue-50 transition-colors"
                                title="Edit category"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDelete("categories", item.id, item.name)
                                }
                                className="text-red-500 hover:text-red-700 p-1 sm:p-2 rounded hover:bg-red-50 transition-colors"
                                title="Delete category"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}

                        {activeTab === "activities" && (
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              <div
                                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex-shrink-0 mt-1"
                                style={{ backgroundColor: item.category.color }}
                              ></div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-2 mb-1">
                                  <h3 className="font-medium text-gray-800 text-sm sm:text-base">
                                    {item.name}
                                  </h3>
                                  {(!item.isActive ||
                                    (item.activeFrom &&
                                      new Date(item.activeFrom) > new Date()) ||
                                    (item.activeUntil &&
                                      new Date(item.activeUntil) <
                                        new Date())) && (
                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium mt-1 sm:mt-0 inline-block">
                                      Unavailable
                                    </span>
                                  )}
                                  {item.requiresDescription && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium mt-1 sm:mt-0 inline-block">
                                      Requires Description
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs sm:text-sm text-gray-500">
                                  {item.category.name}
                                </p>
                                {item.description && (
                                  <p className="text-xs text-gray-400 mt-1 break-words">
                                    {item.description}
                                  </p>
                                )}

                                {/* Show description prompt if required */}
                                {item.requiresDescription &&
                                  item.descriptionPrompt && (
                                    <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                                      <span className="text-blue-600 font-medium">
                                        Required info:{" "}
                                      </span>
                                      <span className="text-blue-700">
                                        {item.descriptionPrompt}
                                      </span>
                                    </div>
                                  )}

                                {(item.activeFrom || item.activeUntil) && (
                                  <div className="flex items-center space-x-2 text-xs text-gray-500 mt-2">
                                    <Calendar className="w-3 h-3 flex-shrink-0" />
                                    <span className="break-words">
                                      {item.activeFrom &&
                                        `From ${new Date(
                                          item.activeFrom
                                        ).toLocaleDateString("en-US", {
                                          timeZone: "UTC",
                                        })}`}
                                      {item.activeFrom &&
                                        item.activeUntil &&
                                        " • "}
                                      {item.activeUntil &&
                                        `Until ${new Date(
                                          item.activeUntil
                                        ).toLocaleDateString("en-US", {
                                          timeZone: "UTC",
                                        })}`}
                                    </span>
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <div
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      item.isActive
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {item.isActive ? "Active" : "Inactive"}
                                  </div>
                                  <div className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                    {item._count?.timeEntries || 0} entries
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 self-start">
                              <button
                                onClick={() => openModal("activity", item)}
                                className="text-blue-500 hover:text-blue-700 p-1 sm:p-2 rounded hover:bg-blue-50 transition-colors"
                                title="Edit activity"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDelete("activities", item.id, item.name)
                                }
                                className="text-red-500 hover:text-red-700 p-1 sm:p-2 rounded hover:bg-red-50 transition-colors"
                                title="Delete activity"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {filteredData().length === 0 && (
                      <div className="text-center py-8 sm:py-12 text-gray-500">
                        <p className="text-sm sm:text-base">
                          No {activeTab} found
                        </p>
                        {searchTerm && (
                          <p className="text-xs sm:text-sm mt-1">
                            Try adjusting your search terms
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          {activeTab === "suggestions" && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">
                Activity Suggestions
              </h3>

              {loading ? (
                <div className="flex justify-center items-center py-8 sm:py-12">
                  <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-2 border-blue-600 border-t-transparent"></div>
                </div>
              ) : activitySuggestions.length === 0 ? (
                <div className="text-center py-8 sm:py-12 text-gray-500">
                  <Plus className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm sm:text-base">
                    No activity suggestions yet
                  </p>
                  <p className="text-xs sm:text-sm">
                    Users can suggest new activities from the main app
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activitySuggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-medium text-gray-800 text-sm sm:text-base">
                                {suggestion.name}
                              </h3>
                              <div
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  suggestion.status === "pending"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : suggestion.status === "approved"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {suggestion.status}
                              </div>
                              {suggestion.isYearLong && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                  Year-Long
                                </span>
                              )}
                            </div>

                            <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                              <p>
                                <strong>Suggested by:</strong>{" "}
                                {suggestion.user.firstName}{" "}
                                {suggestion.user.lastName} (
                                {suggestion.user.email})
                              </p>
                              <p>
                                <strong>Date:</strong>{" "}
                                {new Date(
                                  suggestion.createdAt
                                ).toLocaleDateString()}
                              </p>
                              {suggestion.category && (
                                <p>
                                  <strong>Category:</strong>{" "}
                                  {suggestion.category.name}
                                </p>
                              )}
                              {!suggestion.isYearLong &&
                                (suggestion.activeFrom ||
                                  suggestion.activeUntil) && (
                                  <p>
                                    <strong>Suggested Dates:</strong>
                                    {suggestion.activeFrom &&
                                      ` From ${new Date(
                                        suggestion.activeFrom
                                      ).toLocaleDateString()}`}
                                    {suggestion.activeUntil &&
                                      ` Until ${new Date(
                                        suggestion.activeUntil
                                      ).toLocaleDateString()}`}
                                  </p>
                                )}
                            </div>

                            {suggestion.description && (
                              <div className="mt-2 p-2 bg-gray-50 rounded text-xs sm:text-sm">
                                <strong>Description:</strong>{" "}
                                {suggestion.description}
                              </div>
                            )}

                            {suggestion.reason && (
                              <div className="mt-2 p-2 bg-blue-50 rounded text-xs sm:text-sm">
                                <strong>Reason:</strong> {suggestion.reason}
                              </div>
                            )}
                          </div>

                          {suggestion.status === "pending" && (
                            <div className="flex bg-gray-100 rounded-lg p-1 space-x-1 flex-shrink-0 ml-3">
                              {/* Approve Button in Group */}
                              <button
                                onClick={() =>
                                  handleApproveSuggestion(suggestion)
                                }
                                className="group relative bg-white hover:bg-green-50 text-green-600 hover:text-green-700 px-3 py-2 rounded-md transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md flex items-center space-x-1 border border-transparent hover:border-green-200"
                                title="Approve and create activity"
                              >
                                <CheckCircle className="w-4 h-4 group-hover:rotate-12 transition-transform duration-200" />
                                <span className="hidden sm:inline text-sm font-medium">
                                  Approve
                                </span>

                                {/* Success indicator */}
                                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                              </button>

                              {/* Reject Button in Group */}
                              <button
                                onClick={() =>
                                  handleRejectSuggestion(suggestion)
                                }
                                className="group relative bg-white hover:bg-red-50 text-red-600 hover:text-red-700 px-3 py-2 rounded-md transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md flex items-center space-x-1 border border-transparent hover:border-red-200"
                                title="Reject suggestion"
                              >
                                <X className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
                                <span className="hidden sm:inline text-sm font-medium">
                                  Reject
                                </span>

                                {/* Warning indicator */}
                                <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                              </button>
                            </div>
                          )}

                          <button
                            onClick={() => handleDeleteSuggestion(suggestion)}
                            className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                            title="Delete suggestion"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Forms */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                  {editingItem
                    ? `Edit ${
                        modalType.charAt(0).toUpperCase() + modalType.slice(1)
                      }`
                    : `Create ${
                        modalType.charAt(0).toUpperCase() + modalType.slice(1)
                      }`}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  disabled={modalLoading}
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* User Form */}
              {modalType === "user" && (
                <form
                  onSubmit={editingItem ? handleUpdateUser : handleCreateUser}
                  className="p-4 sm:p-6 space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={userForm.firstName}
                        onChange={(e) =>
                          setUserForm({
                            ...userForm,
                            firstName: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={modalLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={userForm.lastName}
                        onChange={(e) =>
                          setUserForm({ ...userForm, lastName: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={modalLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={userForm.email}
                      onChange={(e) =>
                        setUserForm({ ...userForm, email: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={modalLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password {editingItem && "(leave blank to keep current)"}
                      {!editingItem && " *"}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        required={!editingItem}
                        value={userForm.password}
                        onChange={(e) =>
                          setUserForm({ ...userForm, password: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10 text-sm"
                        disabled={modalLoading}
                        placeholder={
                          editingItem
                            ? "Leave blank to keep current password"
                            : ""
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                        disabled={modalLoading}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <select
                        value={userForm.role}
                        onChange={(e) =>
                          setUserForm({ ...userForm, role: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={modalLoading}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={userForm.isActive}
                        onChange={(e) =>
                          setUserForm({
                            ...userForm,
                            isActive: e.target.value === "true",
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={modalLoading}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School Access {!editingItem && <span className="text-red-500">*</span>}
                    </label>
                    <div className="space-y-2">
                      {/* Multi-select dropdown */}
                      <select
                        onChange={(e) => {
                          const schoolId = e.target.value;
                          if (schoolId && !userForm.schoolIds.includes(schoolId)) {
                            setUserForm({
                              ...userForm,
                              schoolIds: [...userForm.schoolIds, schoolId],
                            });
                          }
                          e.target.value = ""; // Reset dropdown
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                          !editingItem && userForm.schoolIds.length === 0
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                        }`}
                        disabled={modalLoading}
                      >
                        <option value="">
                          {userForm.schoolIds.length === 0
                            ? '⚠️ Select at least one school...'
                            : 'Add more schools...'}
                        </option>
                        {schools
                          .filter((school) => !userForm.schoolIds.includes(school.id))
                          .map((school) => (
                            <option key={school.id} value={school.id}>
                              {school.name}
                            </option>
                          ))}
                      </select>

                      {/* Selected schools as chips */}
                      {userForm.schoolIds.length > 0 && (
                        <div className="flex flex-wrap gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                          {userForm.schoolIds.map((schoolId) => {
                            const school = schools.find((s) => s.id === schoolId);
                            return school ? (
                              <div
                                key={schoolId}
                                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm"
                              >
                                <Building2 className="w-3.5 h-3.5" />
                                <span className="font-medium">{school.name}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setUserForm({
                                      ...userForm,
                                      schoolIds: userForm.schoolIds.filter(
                                        (id) => id !== schoolId
                                      ),
                                    });
                                  }}
                                  className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                  disabled={modalLoading}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : null;
                          })}
                        </div>
                      )}

                      {userForm.schoolIds.length === 0 && (
                        <div className={`p-3 rounded-lg text-sm ${
                          !editingItem
                            ? 'bg-red-50 border border-red-200 text-red-700'
                            : 'bg-gray-50 text-gray-500'
                        }`}>
                          <p className={!editingItem ? 'font-medium' : 'italic'}>
                            {!editingItem
                              ? '⚠️ Required: Please select at least one school. User will not have access without school assignment.'
                              : 'No schools selected. User will not have access to any schools.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                      disabled={modalLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2 text-sm"
                      disabled={modalLoading}
                    >
                      {modalLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      )}
                      <Save className="w-4 h-4" />
                      <span>{editingItem ? "Update" : "Create"}</span>
                    </button>
                  </div>
                </form>
              )}

              {/* School Form */}
              {modalType === "school" && (
                <form
                  onSubmit={editingItem ? handleUpdateSchool : handleCreateSchool}
                  className="p-4 sm:p-6 space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={schoolForm.name}
                      onChange={(e) =>
                        setSchoolForm({
                          ...schoolForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={modalLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School Logo
                    </label>

                    {/* Show current logo if editing */}
                    {editingItem && editingItem.logoUrl && !schoolLogoFile && (
                      <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600 mb-2">Current logo:</p>
                        <img
                          src={editingItem.logoUrl}
                          alt="Current school logo"
                          className="h-16 w-16 object-cover rounded-lg"
                        />
                      </div>
                    )}

                    {/* File upload input */}
                    <div className="flex items-center space-x-3">
                      <label className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                          <Upload className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">
                            {schoolLogoFile ? schoolLogoFile.name : "Choose logo file"}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setSchoolLogoFile(e.target.files[0])}
                          className="hidden"
                          disabled={modalLoading}
                        />
                      </label>
                      {schoolLogoFile && (
                        <button
                          type="button"
                          onClick={() => setSchoolLogoFile(null)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    {/* Preview new logo */}
                    {schoolLogoFile && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs text-green-700 mb-2">New logo preview:</p>
                        <img
                          src={URL.createObjectURL(schoolLogoFile)}
                          alt="Logo preview"
                          className="h-16 w-16 object-cover rounded-lg"
                        />
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2">
                      Upload an image file (PNG, JPG, etc.) for the school logo. Recommended size: 500x500px
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                      disabled={modalLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2 text-sm"
                      disabled={modalLoading}
                    >
                      {modalLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      )}
                      <Save className="w-4 h-4" />
                      <span>{editingItem ? "Update" : "Create"}</span>
                    </button>
                  </div>
                </form>
              )}

              {/* Category Form */}
              {modalType === "category" && (
                <form
                  onSubmit={
                    editingItem ? handleUpdateCategory : handleCreateCategory
                  }
                  className="p-4 sm:p-6 space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School *
                    </label>
                    <select
                      required
                      value={categoryForm.schoolId}
                      onChange={(e) =>
                        setCategoryForm({
                          ...categoryForm,
                          schoolId: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={modalLoading}
                    >
                      <option value="">Select a school</option>
                      {schools.map((school) => (
                        <option key={school.id} value={school.id}>
                          {school.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={categoryForm.name}
                      onChange={(e) =>
                        setCategoryForm({
                          ...categoryForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={modalLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={categoryForm.description}
                      onChange={(e) =>
                        setCategoryForm({
                          ...categoryForm,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                      disabled={modalLoading}
                      placeholder="Optional description for this category"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={categoryForm.color}
                        onChange={(e) =>
                          setCategoryForm({
                            ...categoryForm,
                            color: e.target.value,
                          })
                        }
                        className="w-10 h-8 sm:w-12 sm:h-10 border border-gray-300 rounded cursor-pointer"
                        disabled={modalLoading}
                      />
                      <input
                        type="text"
                        value={categoryForm.color}
                        onChange={(e) =>
                          setCategoryForm({
                            ...categoryForm,
                            color: e.target.value,
                          })
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={modalLoading}
                        placeholder="#3498db"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                      disabled={modalLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2 text-sm"
                      disabled={modalLoading}
                    >
                      {modalLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      )}
                      <Save className="w-4 h-4" />
                      <span>{editingItem ? "Update" : "Create"}</span>
                    </button>
                  </div>
                </form>
              )}

              {/* Activity Form - Updated with Description Requirements */}
              {modalType === "activity" && (
                <form
                  onSubmit={
                    editingItem?.type === "suggestion"
                      ? handleCreateActivity // Always use create for suggestions
                      : editingItem?.id
                      ? handleUpdateActivity // Use update for existing activities
                      : handleCreateActivity // Use create for new activities
                  }
                  className="p-4 sm:p-6 space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activity Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={activityForm.name}
                      onChange={(e) =>
                        setActivityForm({
                          ...activityForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={modalLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School *
                    </label>
                    <select
                      required
                      value={activityForm.schoolId}
                      onChange={(e) =>
                        setActivityForm({
                          ...activityForm,
                          schoolId: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={modalLoading}
                    >
                      <option value="">Select a school</option>
                      {schools.map((school) => (
                        <option key={school.id} value={school.id}>
                          {school.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      required
                      value={activityForm.categoryId}
                      onChange={(e) =>
                        setActivityForm({
                          ...activityForm,
                          categoryId: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={modalLoading}
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={activityForm.description}
                      onChange={(e) =>
                        setActivityForm({
                          ...activityForm,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                      disabled={modalLoading}
                      placeholder="Optional description for this activity"
                    />
                  </div>

                  {/* Year-Long Activity Option */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={activityForm.isYearLong}
                            onChange={(e) =>
                              setActivityForm({
                                ...activityForm,
                                isYearLong: e.target.checked,
                                // Clear dates when switching to year-long
                                activeFrom: e.target.checked
                                  ? ""
                                  : activityForm.activeFrom,
                                activeUntil: e.target.checked
                                  ? ""
                                  : activityForm.activeUntil,
                              })
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            disabled={modalLoading}
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            This is a year-long activity
                          </span>
                        </label>
                      </div>

                      {activityForm.isYearLong && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg
                                className="h-5 w-5 text-blue-400"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800">
                                Year-Long Activity
                              </h3>
                              <div className="mt-2 text-sm text-blue-700">
                                <p>
                                  This activity will be available throughout the
                                  year. The start and end date fields are
                                  disabled for year-long activities.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {!activityForm.isYearLong && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <p className="text-sm text-gray-600">
                            <strong>Date-specific activity:</strong> Set
                            optional start and end dates to control when this
                            activity is available for time logging.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date Fields - Disabled when year-long */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Active From{" "}
                        {activityForm.isYearLong ? "(Disabled)" : "(Optional)"}
                      </label>
                      <input
                        type="date"
                        value={activityForm.activeFrom}
                        onChange={(e) =>
                          setActivityForm({
                            ...activityForm,
                            activeFrom: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                          activityForm.isYearLong
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={modalLoading || activityForm.isYearLong}
                      />
                      {activityForm.isYearLong && (
                        <p className="text-xs text-gray-500 mt-1">
                          Start date is not needed for year-long activities
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Active Until{" "}
                        {activityForm.isYearLong ? "(Disabled)" : "(Optional)"}
                      </label>
                      <input
                        type="date"
                        value={activityForm.activeUntil}
                        onChange={(e) =>
                          setActivityForm({
                            ...activityForm,
                            activeUntil: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                          activityForm.isYearLong
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={
                          modalLoading ||
                          activityForm.isYearLong ||
                          !activityForm.activeFrom
                        }
                        min={activityForm.activeFrom}
                      />
                      {activityForm.isYearLong && (
                        <p className="text-xs text-gray-500 mt-1">
                          End date is not needed for year-long activities
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description Requirement Section */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={activityForm.requiresDescription}
                            onChange={(e) =>
                              setActivityForm({
                                ...activityForm,
                                requiresDescription: e.target.checked,
                                descriptionPrompt: e.target.checked
                                  ? activityForm.descriptionPrompt
                                  : "",
                              })
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            disabled={modalLoading}
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            Require description when logging time for this
                            activity
                          </span>
                        </label>
                      </div>

                      {activityForm.requiresDescription && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description Prompt *
                          </label>
                          <input
                            type="text"
                            required={activityForm.requiresDescription}
                            value={activityForm.descriptionPrompt}
                            onChange={(e) =>
                              setActivityForm({
                                ...activityForm,
                                descriptionPrompt: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            disabled={modalLoading}
                            placeholder="e.g., 'Grade and teacher name' or 'Event details and location'"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            This text will be shown to users as guidance for
                            what information to include
                          </p>
                        </div>
                      )}

                      {activityForm.requiresDescription && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg
                                className="h-5 w-5 text-blue-400"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800">
                                Description Requirement Preview
                              </h3>
                              <div className="mt-2 text-sm text-blue-700">
                                <p>
                                  When users select this activity, they will
                                  see:
                                </p>
                                <p className="mt-1 italic">
                                  "*{" "}
                                  {activityForm.descriptionPrompt ||
                                    "Description"}{" "}
                                  (Required for{" "}
                                  {activityForm.name || "This Activity"})"
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={activityForm.isActive}
                      onChange={(e) =>
                        setActivityForm({
                          ...activityForm,
                          isActive: e.target.value === "true",
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={modalLoading}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                      disabled={modalLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2 text-sm"
                      disabled={
                        modalLoading ||
                        (activityForm.requiresDescription &&
                          !activityForm.descriptionPrompt)
                      }
                    >
                      {modalLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      )}
                      <Save className="w-4 h-4" />
                      <span>{editingItem ? "Update" : "Create"}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .thin-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 transparent;
        }
        .thin-scrollbar::-webkit-scrollbar {
          height: 6px;
        }
        .thin-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .thin-scrollbar::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 9999px;
        }
      `}</style>

      {/* Reject Suggestion Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Reject Suggestion
              </h3>
              <button
                onClick={closeActionModals}
                className="text-gray-400 hover:text-gray-600"
                disabled={actionLoading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                You are rejecting the suggestion:{" "}
                <strong>"{selectedSuggestion?.name}"</strong>
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for rejection (will be visible to the user)
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="e.g., This activity already exists as 'Library Volunteer' or similar activities are covered under 'General Events'"
                  maxLength={500}
                  disabled={actionLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {actionReason.length}/500 characters
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-700">
                  The user will see this reason in their suggestion dashboard.
                  Be constructive and helpful in your feedback.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={closeActionModals}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRejectSuggestion}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 flex items-center justify-center space-x-2"
                  disabled={actionLoading}
                >
                  {actionLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  )}
                  <span>
                    {actionLoading ? "Rejecting..." : "Reject Suggestion"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Suggestion Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Delete Suggestion
              </h3>
              <button
                onClick={closeActionModals}
                className="text-gray-400 hover:text-gray-600"
                disabled={actionLoading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                You are deleting the suggestion:{" "}
                <strong>"{selectedSuggestion?.name}"</strong>
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for deletion (optional - will be visible to the user)
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="e.g., Duplicate suggestion or inappropriate content"
                  maxLength={500}
                  disabled={actionLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {actionReason.length}/500 characters
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <div className="text-sm text-red-700">
                  <p className="font-medium mb-1">Deletion options:</p>
                  <p>
                    • <strong>With reason:</strong> Marks as deleted, user can
                    see the reason
                  </p>
                  <p>
                    • <strong>Without reason:</strong> Permanently removes from
                    database
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={closeActionModals}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteSuggestion}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 flex items-center justify-center space-x-2"
                  disabled={actionLoading}
                >
                  {actionLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  )}
                  <span>
                    {actionLoading ? "Deleting..." : "Delete Suggestion"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;